import { useEffect, useRef, useState } from 'react';
import { formatDescription, formatNumber, formatTimeDifference, getInitials, formatBadge, getCachedMediaUrl } from '../../../lib/utils/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import VerticalDotsBlack from '../../../Assets/VerticalDotsBlack.svg';
import StarRating from '../../../Components/StarRating/StarRating';
import { checkUserLikeOnPost, getPostComments, getPostProfile, getProductByID, likePost, unlikePost } from '../../../lib/utils/apiCalls';
import Prev from '../../../Assets/Modals/Prev.svg';
import Next from '../../../Assets/Modals/Next.svg';
import Like from '../../../Assets/Lens/Like.svg';
import LikeGreen from '../../../Assets/Lens/LikeGreen.svg';
import SoundFull from '../../../Assets/Lens/RadioIcon.svg';
import SoundMute from '../../../Assets/Lens/RadioMute.svg';
import Share from '../../../Assets/Lens/Share.svg';
import Comment from '../../../Assets/Lens/Comment.svg';

import { useDispatch, useSelector } from 'react-redux';
import KebabMenu from './KebabMenu';
import { closeViewerKebabMenu, openViewerKebabMenu, setModal, setSelectedPost } from '../../../Features/lensSlice';
import { useOutsideClickForTwoRef } from '../../../Hooks/useOutsideClickForTwoRef';

function ReviewPost({ post }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const kebabButtonRef = useRef(null);
    const kebabRef = useRef(null);

    useOutsideClickForTwoRef(kebabRef, kebabButtonRef, () => {
        dispatch(closeViewerKebabMenu());
    });

    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const mediaContainerRef = useRef(null);

    const [mediaContainerHeight, setMediaContainerHeight] = useState(0);
    const [isMuted, setIsMuted] = useState(true); // start muted (autoplay-friendly)
    const userSetAudioRef = useRef(false);        // tracks if user explicitly changed audio
    const [product, setProduct] = useState({});
    const [userProfile, setUserProfile] = useState({});
    const [comments, setComments] = useState([]);
    const [liked, setLiked] = useState(false);
    const [displayFullDescription, setDisplayFullDescription] = useState(false);
    const [slide, setSlide] = useState(0);
    const [likesCount, setLikesCount] = useState(0);
    const [shutterVisible, setShutterVisible] = useState(false);

    const kebabPostID = useSelector((state) => state.lens.viewerKebab.postID);

    const hasImages = Array.isArray(post.imageUrls) && post.imageUrls.length > 0;
    const hasSingleImage = Array.isArray(post.imageUrls) && post.imageUrls.length === 1;
    const hasMultipleImages = Array.isArray(post.imageUrls) && post.imageUrls.length > 1;
    const hasVideo = Boolean(post.videoUrls);

    const handleLikeClick = async () => {
        try {
            if (liked) {
                const likeCount = await unlikePost(post.id);
                setLikesCount(likeCount.noOfLikes);
            } else {
                const likeCount = await likePost(post.id);
                setLikesCount(likeCount.noOfLikes);
            }
            const response = await checkUserLikeOnPost(post.id);
            setLiked(response);
        } catch (err) {
            console.log(err);
        }
    };

    const handleOpenKebab = () => {
        if (kebabPostID === post.id) {
            dispatch(closeViewerKebabMenu());
        } else {
            dispatch(openViewerKebabMenu(post.id));
        }
    };

    const handleShareButton = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
        dispatch(setSelectedPost(post));
        dispatch(setModal('share'));
    };

    const handleDescriptionLength = () => {
        setDisplayFullDescription((v) => !v);
    };

    // Toggle respects + records user choice
    const toggleAudio = () => {
        const v = videoRef.current;
        if (!v) return;
        const next = !v.muted;
        v.muted = next;
        setIsMuted(next);
        userSetAudioRef.current = true; // user explicitly chose
    };

    // Play helper: honors current isMuted; falls back to muted only if autoplay w/ sound is blocked
    const playVideo = (fromUser = false) => {
        const v = videoRef.current;
        if (!v) return;

        v.muted = isMuted;

        v.play().catch((err) => {
            // If not from direct user action AND sound is on, browsers can block it.
            // Fallback to muted autoplay, but keep user's choice in state (don't flip it permanently).
            if (!fromUser && isMuted === false) {
                v.muted = true;
                v.play().catch(() => {});
            } else {
                console.error('Video play error:', err);
            }
        });
    };

    const handleWatchAgain = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShutterVisible(false);
        // This is a user gesture => allow unmuted play per current isMuted
        playVideo(true);
    };

    const forwardSlide = () => {
        if (!hasMultipleImages) return;
        setSlide((prev) => (prev === post.imageUrls.length - 1 ? 0 : prev + 1));
    };

    const backwardSlide = () => {
        if (!hasMultipleImages) return;
        setSlide((prev) => (prev === 0 ? post.imageUrls.length - 1 : prev - 1));
    };

    const navigateToProfile = () => {
        if (userProfile && Object.keys(userProfile).length > 0) {
            const id = post.SellerUserId || post.UserId;
            navigate(`${location.pathname}/profile/${id}`);
        } else {
            console.error('userProfile is empty or not defined');
        }
    };

    const handlePostClick = () => {
        if (videoRef.current) {
            videoRef.current.pause();
        }
        dispatch(setSelectedPost(post));
        dispatch(setModal('post'));
    };

    useEffect(() => {
        setLikesCount(post.noOflikes);
        const userId = post.UserId || post.SellerUserId;

        const getProduct = async () => {
            try {
                const response = await getProductByID(post.productId);
                if (response?.status === 200) {
                    setProduct(response?.data?.product);
                }
            } catch (e) {
                console.log(e);
            }
        };

        const getUserProfile = async () => {
            try {
                const profile = await getPostProfile(userId);
                setUserProfile(profile || {});
            } catch (e) {
                console.log(e);
            }
        };

        const getUserLiked = async () => {
            try {
                const response = await checkUserLikeOnPost(post.id);
                setLiked(response);
            } catch (e) {
                console.log(e);
            }
        };

        const getComments = async () => {
            try {
                const response = await getPostComments(post.id);
                setComments(response || []);
            } catch (e) {
                console.log(e);
            }
        };

        const timer = setTimeout(() => {
            if (Object.keys(post || {}).length > 0) {
                if (post.productId) getProduct();
                getComments();
                getUserLiked();
                if (userId) getUserProfile();
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [post]);

    // Intersection observer: do NOT force-mute on enter; honor current isMuted and fallback if blocked
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        const handleIntersection = (entries) => {
            entries.forEach((entry) => {
                const vid = videoRef.current;
                if (!vid) return;

                if (!entry.isIntersecting) {
                    if (!vid.paused) vid.pause();
                } else {
                    setShutterVisible(false);
                    // Try to play with current isMuted. If browser blocks unmuted autoplay,
                    // playVideo(false) will fallback to muted (without erasing user's preference).
                    playVideo(false);
                }
            });
        };

        const observer = new IntersectionObserver(handleIntersection, { threshold: 0.1 });
        observer.observe(v);
        return () => observer.disconnect();
    }, [isMuted, hasVideo]);

    // Square media box: keep height = width with ResizeObserver
    useEffect(() => {
        const node = mediaContainerRef.current;
        if (!node) return;

        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect.width;
                setMediaContainerHeight(w);
            }
        });
        ro.observe(node);

        // initialize once
        setMediaContainerHeight(node.clientWidth);

        return () => {
            ro.disconnect();
        };
    }, []);

    // Show shutter on video end
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const handleEnded = () => setShutterVisible(true);
        v.addEventListener('ended', handleEnded);
        return () => v.removeEventListener('ended', handleEnded);
    }, [hasVideo]);

    return (
        <div className='rounded-[9px] w-full flex flex-col gap-2 p-4 border border-[#EAEAEA] bg-[#FFFFFF]'>
            <div className="flex flex-col gap-[5px] w-full">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-1">
                        <div className="size-[50px] rounded-full overflow-hidden">
                            {userProfile?.ProfilePic ? (
                                <img
                                    className='size-full object-cover'
                                    src={getCachedMediaUrl(userProfile.ProfilePic)}
                                    alt=""
                                />
                            ) : (
                                <div className='size-full bg-[#EAEAEA] flex items-center justify-center'>
                                    <p className='font-mulish font-bold text-base text-[#666666]'>
                                        {getInitials(
                                            post.UserId
                                                ? userProfile?.fullName || ''
                                                : userProfile?.name || ''
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col text-[#121212]">
                            {Object.keys(userProfile || {}).length > 0 ? (
                                <h1
                                    onClick={navigateToProfile}
                                    className='hover:underline select-none cursor-pointer w-fit font-semibold text-[16px] leading-[26px]'
                                >
                                    {post.UserId ? userProfile.fullName : userProfile.name}
                                </h1>
                            ) : (
                                <div className='w-36 h-[22px] bg-[linear-gradient(90deg,_rgba(235,235,235,1)_0%,_rgba(255,255,255,1)_100%)]'></div>
                            )}

                            {Object.keys(userProfile || {}).length > 0 ? (
                                <p className='font-normal select-none text-[14px] leading-6'>
                                    {post.UserId
                                        ? userProfile.milestone
                                            ? userProfile.milestone === 'No Review'
                                                ? userProfile.milestone
                                                : `${formatBadge(userProfile.milestone)}`
                                            : ''
                                        : `${userProfile.favourite ? formatNumber(userProfile.favourite) : '0'} Shoppers' Favorite`}
                                </p>
                            ) : (
                                <div className='h-6 bg-white flex items-center'>
                                    <div className='w-24 h-[20px] bg-[linear-gradient(90deg,_rgba(235,235,235,1)_0%,_rgba(255,255,255,1)_100%)]'></div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <div className="flex items-center gap-1">
                            <div className="size-1 rounded-full bg-[#121212]"></div>
                            <p className='font-normal select-none text-[12px] leading-6 text-[#121212]'>
                                {formatTimeDifference(post.createdAt)} ago
                            </p>
                        </div>

                        <div className="relative size-6 flex items-center justify-center">
                            <button
                                ref={kebabButtonRef}
                                className='select-none outline-none border-none size-full flex items-center justify-center'
                                onClick={handleOpenKebab}
                            >
                                <img src={VerticalDotsBlack} alt="" />
                            </button>

                            {kebabPostID === post.id && (
                                <KebabMenu boxRef={kebabRef} user={userProfile} post={post} />
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-[2px]">
                    <StarRating rating={post.ratings > 0 ? post.ratings : 0} />

                    <p className='font-semibold text-[18px] leading-[30px]'>{post?.postTitle}</p>
                </div>

                {post.postDesc && (
                    <div className="relative">
                        <p
                            className={`flex-grow flex-wrap font-normal text-[14px] leading-[26px] ${
                                post.postDesc.split(' ').length > 15 && 'mr-14'
                            }`}
                        >
                            {!displayFullDescription ? (
                                post.postDesc.split(' ').length > 15 ? (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: formatDescription(
                                                post.postDesc.split(' ').slice(0, 15).join(' ') + '...'
                                            )
                                        }}
                                    />
                                ) : (
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: formatDescription(post.postDesc)
                                        }}
                                    />
                                )
                            ) : (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: formatDescription(post.postDesc)
                                    }}
                                />
                            )}
                        </p>

                        {post.postDesc.split(' ').length > 15 && (
                            <button
                                onClick={handleDescriptionLength}
                                className='hover:underline absolute bottom-0 right-0 font-semibold text-[14px] leading-[26px] text-[#1055EB]'
                            >
                                {displayFullDescription ? 'see less' : 'see more'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div className="w-full flex flex-col gap-4">
                {(hasSingleImage || hasVideo) && (
                    <div
                        ref={mediaContainerRef}
                        className="w-full relative cursor-pointer flex items-center justify-center"
                        style={{ height: `${mediaContainerHeight}px` }}
                    >
                        {hasSingleImage && (
                            <img
                                onClick={handlePostClick}
                                className='max-w-full max-h-full rounded-lg'
                                key={post.imageUrls[0]}
                                src={getCachedMediaUrl(post.imageUrls[0])}
                                alt=""
                            />
                        )}

                        {hasVideo && (
                            <div
                                ref={containerRef}
                                className='w-[99.9%] relative h-full flex items-center justify-center'
                            >
                                <video
                                    onClick={handlePostClick}
                                    ref={videoRef}
                                    className='max-w-full max-h-full rounded-lg'
                                    src={post.videoUrls}
                                    playsInline
                                    preload="metadata"
                                />
                                <button
                                    onClick={toggleAudio}
                                    className="select-none z-10 absolute bottom-4 right-4 size-8 flex items-center justify-center rounded-full bg-[#F5F5F5]"
                                >
                                    <div className="size-[18px] flex items-center justify-center">
                                        <img src={isMuted ? SoundMute : SoundFull} alt="Sound" />
                                    </div>
                                </button>

                                <div
                                    onClick={handlePostClick}
                                    className={`${
                                        shutterVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
                                    } transition-all duration-75 ease-in-out size-full flex items-center justify-center bg-[#000000AB] absolute top-0 left-0 z-0 rounded-lg`}
                                >
                                    <button
                                        className="text-[16px] font-bold leading-6 text-[#FFFFFF]"
                                        onClick={handleWatchAgain}
                                    >
                                        Watch again
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {hasMultipleImages && (
                    <div
                        ref={mediaContainerRef}
                        className="w-full relative cursor-pointer flex items-center justify-center"
                        style={{ height: `${mediaContainerHeight}px` }}
                    >
                        {post.imageUrls.map((url, index) => {
                            if (!url) return null;
                            return (
                                <img
                                    onClick={handlePostClick}
                                    className={`max-w-full max-h-full rounded-lg ${
                                        slide === index ? '' : 'hidden'
                                    }`}
                                    key={url}
                                    src={getCachedMediaUrl(url)}
                                    alt=""
                                />
                            );
                        })}

                        <button
                            onClick={backwardSlide}
                            className="rounded-full bg-[#FFFFFF8A] hover:bg-[#FFFFFFBC] absolute top-1/2 left-3 -translate-y-1/2 size-11 flex items-center justify-center"
                        >
                            <img src={Prev} alt="" />
                        </button>

                        <button
                            onClick={forwardSlide}
                            className="rounded-full bg-[#FFFFFF8A] hover:bg-[#FFFFFFBC] absolute top-1/2 right-3 -translate-y-1/2 size-11 flex items-center justify-center"
                        >
                            <img src={Next} alt="" />
                        </button>
                    </div>
                )}

                {Object.keys(product).length > 0 && (
                    <Link to={`/inventory-management/product/${product?.id}`} className="w-full flex rounded-lg overflow-hidden cursor-pointer transition-all delay-75 ease-in hover:opacity-75">
                        <div className="size-[88px]">
                            {product?.productImgUrls && product?.productImgUrls[0] && (
                                <img
                                    className='size-full object-cover'
                                    src={getCachedMediaUrl(product.productImgUrls[0])}
                                    alt=""
                                />
                            )}
                        </div>

                        <div className="w-[calc(100%-84px)] bg-[#808080] p-1 flex flex-col justify-center">
                            <h1 className='font-medium text-[18px] leading-7 text-[#FFFFFF]'>
                                {product?.productName}
                            </h1>
                            <p className='w-full font-normal text-base text-[#FFFFFF] whitespace-nowrap overflow-hidden text-ellipsis'>
                                {product?.productDesc}
                            </p>
                        </div>
                    </Link>
                )}

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="flex items-center">
                            <button onClick={handleLikeClick} className="size-11 flex items-center justify-center">
                                <img src={liked ? LikeGreen : Like} alt="" />
                            </button>
                            {typeof likesCount === 'number' && (
                                <p className='select-none font-normal text-[14px] leading-[26px] text-[#121212]'>
                                    {formatNumber(likesCount)}
                                </p>
                            )}
                        </div>

                        <div onClick={handlePostClick} className="flex items-center cursor-pointer">
                            <div className="size-11 flex items-center justify-center">
                                <img src={Comment} alt="" />
                            </div>
                            <p className='select-none font-normal text-[14px] leading-[26px] text-[#121212]'>
                                {formatNumber(comments.length)} Comment{comments.length > 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>

                    <button onClick={handleShareButton} className="w-12 h-11 flex items-center justify-center">
                        <img src={Share} alt="" />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ReviewPost;